import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import isArray from 'lodash.isarray';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import PlayButton from 'assets/svg/play-button.svg';
import FlippingComponent from 'components/FlippingComponent/FlippingComponent';
import VideoPlayer from 'components/VideoPlayerAccessibility';
import { t } from 'translations';
import { formatPlyrVolume } from 'utils/plyr-util';
import InactivityManager from 'utils/InactivityManager';
import { playMultipleSounds } from 'utils/play-multiple-sounds';
import './DidYouKnow.scss';

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const REASON = {
  didNotFlipCard: 'Did not flip card',
  didNotTouchCard: 'Did not touch the card'
};

const CONTAINER_STYLE = {
  width: 'auto',
  height: '100%',
  position: 'absolute',
  top: 0
};

const PLYR_STYLE_OVERRIDE = {
  minWidth: window.innerWidth < 400 ? 'unset !important' : '200px',
  height: '100%'
};

function DidYouKnow({ currentRound, onRoundEnd, lwtStoreState, overlayActive }) {
  const [roundEnd, setRoundEnd] = useState(false);
  const [roundIndex, setRoundIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [isCardFlipped, setIsCardFlipped] = useState(false);

  const vidRef = useRef();
  const timers = useRef([]);
  const promiseRef = useRef(null);

  const inactivityManager = useRef(
    new InactivityManager(TIMEOUT, () => playInstructions(), REASON.didNotFlipCard)
  ).current;

  const { isLiteVersion, hasAccessibilityMode } = useSelector(state => state.ui);

  const isAccessibilityAndLiteMode = isLiteVersion && hasAccessibilityMode;

  const { audio, data } = currentRound;
  const { cardFront, cardBack, rounds, instructions } = data;
  const {
    accessibility: {
      audioSettings,
      closedCaptionsSettings: { showClosedCaptions, closedCaptionSize },
      videoNarrationSettings: { showSettings }
    }
  } = lwtStoreState.settings;

  const clearTimers = useCallback(() => {
    timers.current.forEach(tID => clearTimeout(tID));
    timers.current = [];
  }, []);

  const startInactivityManger = useCallback(
    (reminderObj, reason) => {
      const reminder = audio[reminderObj?.src];
      if (reminder) {
        inactivityManager.watch(TIMEOUT, () => reminder.play(), reason);
      }
    },
    [audio, inactivityManager]
  );

  const playInstructions = useCallback(() => {
    inactivityManager.clear();
    const beforeFlip = instructions.beforeFlip;
    const snd = audio[beforeFlip[beforeFlip.length - 1].src];
    if (snd) {
      snd.play();
      snd.once('end', () => inactivityManager.watch());
    }
  }, [audio, inactivityManager, instructions]);

  const playOneOrMultipleSounds = useCallback(
    async rIndex => {
      const nextRound = rounds[rIndex ?? roundIndex];
      if (!nextRound) return;
      const instructionsData = nextRound.instructions;
      const isInstructionsAnArray = isArray(instructionsData);
      const reminder = isInstructionsAnArray
        ? instructionsData[instructionsData.length - 1]
        : instructionsData;
      if (instructionsData) {
        if (isInstructionsAnArray) {
          await playMultipleSounds(500, audio, instructionsData);
          startInactivityManger(reminder, REASON.didNotTouchCard);
        } else {
          const snd = audio[instructionsData.src];
          if (snd) {
            snd.play();
            snd.once('end', () => startInactivityManger(reminder, REASON.didNotTouchCard));
          }
        }
      } else startInactivityManger(reminder, REASON.didNotTouchCard);
    },
    [audio, rounds, roundIndex, startInactivityManger]
  );

  const onFlip = useCallback(async () => {
    currentRound.stopAllSounds();
    clearTimers();
    inactivityManager.clear();
    await playOneOrMultipleSounds();
    setIsCardFlipped(flipped => !flipped);
  }, [currentRound, clearTimers, inactivityManager, playOneOrMultipleSounds]);

  const onVideoEnded = useCallback(() => {
    setShowVideo(false);

    if (roundIndex + 1 === rounds.length) {
      setRoundEnd(true);
      onRoundEnd();
      clearTimers();
      inactivityManager.clear();
    } else {
      setTimeout(async () => {
        await playOneOrMultipleSounds(roundIndex + 1);
        setRoundIndex(idx => idx + 1);
      }, 500);
    }
  }, [
    roundIndex,
    rounds.length,
    onRoundEnd,
    clearTimers,
    inactivityManager,
    playOneOrMultipleSounds
  ]);

  const handleVideoClick = e => {
    e.stopPropagation();

    const isVideoClick =
      e.target.tagName === 'VIDEO' ||
      Array.from(e.target.classList)?.some(cls => cls.includes('plyr'));

    const instructionsData = rounds[roundIndex + 1]?.instructions;
    inactivityManager.clear();

    if (isVideoClick) {
      startInactivityManger(instructionsData, REASON.didNotTouchCard);
      return;
    }

    if (roundIndex + 1 > rounds.length || roundEnd) return;

    setShowVideo(true);
    const tID = setTimeout(() => {
      const video = Array.from(document.getElementsByTagName('video'))?.[0];
      video?.play();
    }, 1000);
    timers.current.push(tID);

    startInactivityManger(instructionsData, REASON.didNotTouchCard);
  };

  useEffect(() => {
    // componentDidMount logic
    const beforeFlip = instructions.beforeFlip;
    promiseRef.current = playMultipleSounds(500, audio, [beforeFlip[beforeFlip.length - 1]]);
    promiseRef.current.then(() => inactivityManager.watch());
    return () => {
      // componentWillUnmount logic
      inactivityManager.clear();
      currentRound.stopAllSounds();
      clearTimers();
      if (promiseRef.current && promiseRef.current.cancel) promiseRef.current.cancel();
      if (vidRef.current) {
        vidRef.current.muted = true;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardDescriptions = {
    cardFrontDescription: cardFront.description,
    cardBackDescription: cardBack.description
  };

  const eventKeyOnEnterOrSpace = cb => e => {
    if (e.key === 'Enter' || e.keyCode == 32 || e.detail > 0) cb(e);
  };

  //eslint-disable-next-line
  const FaceBack = ({ shouldShowVideo, overlayActive }) => {
    const video = rounds[roundIndex < 0 ? 0 : roundIndex]?.animation;
    return (
      <div className={'did-you-know-image-back'}>
        {!roundEnd && !overlayActive && (
          <img
            aria-label={t('Play')}
            src={PlayButton}
            className={'play-button'}
            onClick={eventKeyOnEnterOrSpace(handleVideoClick)}
            onKeyDown={eventKeyOnEnterOrSpace(handleVideoClick)}
          />
        )}
        <img
          src={cardBack.src}
          alt=""
          onKeyDown={eventKeyOnEnterOrSpace(handleVideoClick)}
          onClick={eventKeyOnEnterOrSpace(handleVideoClick)}
          height={'100%'}
          width={'auto'}
          role="button"
        />
        <VideoPlayer
          src={video?.src}
          title={''}
          captionsSrc={video?.vtt}
          narrationSrc={video?.narrationSrc ?? ''}
          narrationVtt={video?.narrationVtt ?? ''}
          narrationTitle={''}
          autoPlay={false}
          showSettings={isAccessibilityAndLiteMode || showSettings}
          withNativeControls={true}
          initialShowCC={showClosedCaptions}
          initialCCSize={closedCaptionSize}
          initialShowNarration={false}
          onDone={onVideoEnded}
          containerStyle={{
            ...CONTAINER_STYLE,
            visibility: shouldShowVideo ? 'visible' : 'hidden',
            '@media (max-width: 768px)': { left: 'unset' }
          }}
          plyrStyle={PLYR_STYLE_OVERRIDE}
          volume={formatPlyrVolume(audioSettings.volume, audioSettings.muteSounds)}
        />
      </div>
    );
  };

  return (
    <div
      className={'flip-card'}
      aria-hidden={overlayActive ? 'true' : undefined}
      tabIndex={overlayActive ? -1 : undefined}
    >
      <FlippingComponent
        data-test="unit-test-data"
        faceFront={<img className={'did-you-know-image-front'} src={cardFront.src} alt="" />}
        faceFrontProps={{
          disabled: overlayActive || isCardFlipped,
          tabIndex: overlayActive || isCardFlipped ? -1 : 0
        }}
        faceBack={<FaceBack shouldShowVideo={showVideo} overlayActive={overlayActive} />}
        faceBackProps={{
          disabled: overlayActive || !isCardFlipped,
          tabIndex: overlayActive || !isCardFlipped ? -1 : 0,
          'aria-hidden': overlayActive || !isCardFlipped ? 'true' : undefined,
          onKeyDown: eventKeyOnEnterOrSpace(handleVideoClick),
          onClick: eventKeyOnEnterOrSpace(handleVideoClick)
        }}
        onCardTurned={onFlip}
        cardDescriptions={cardDescriptions}
      />
    </div>
  );
}

DidYouKnow.propTypes = {
  currentRound: PropTypes.object,
  onRoundEnd: PropTypes.func,
  lwtStoreState: PropTypes.object,
  overlayActive: PropTypes.bool
};

export default withLwtStore(DidYouKnow);
